import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lei } from "@/shared/format";
import {
  LANGS, addDays, copy, nightCount, nightsPhrase, refusalText, shortDay, todayHere,
  type Refusal, type RentCopy,
} from "./rent-copy";
import { RangeCalendar } from "./RangeCalendar";

/**
 * PAGINA DE VÂNZARE — /r și /r/<slug>.
 *
 * Wave B-3 of docs/platform/REZERVARI.md, and it is modelled directly on the two
 * pages the house already sells from:
 *
 *   twodoveshouse.com/room/chirie-pensiune/
 *   twodoveshouse.com/room/chirie-gradina-pentru-eveniment/
 *
 * ==========================================================================
 * IT HAS NO ROUTE OF ITS OWN. THAT IS THE POINT.
 * ==========================================================================
 *
 * Everything below is fetched from `/api/v1/*` — the public, versioned,
 * cookie-free tree — with no server-side shortcut, no snapshot read of its own
 * and no secret in the shell. §6 makes that a design rule rather than a promise:
 * if the hosted page can live on the public API alone, then the WordPress plugin
 * the owner asked for is already possible, because the API it needs is the one
 * real guests have been using all season. The moment this file fetches
 * `/api/something-internal`, that guarantee is gone and nobody notices until the
 * day somebody tries to write the plugin.
 *
 * MOBILE FIRST, LITERALLY (§1): drawn for a 390px screen held in one hand on
 * rural 3G, because the link arrives through Instagram or a WhatsApp group. The
 * desktop layout is the same page widened — the two-column shape their own site
 * uses, dates on the left, listings on the right — and not a second design.
 *
 * WHAT IS DELIBERATELY ABSENT: the payment step. The money path is frozen and
 * gets its own commit (worker/api/v1.ts says so); until it lands, this page ends
 * at a recorded reservation the house confirms, and says exactly that rather
 * than showing a „Plătește" button that leads nowhere.
 */

/* ------------------------------------------------------------------ types */

type Rules = {
  min_nights: number;
  max_nights: number;
  lead_days_min: number;
  lead_days_max: number;
  arrival_days: string;
  departure_days: string;
  min_guests: number;
  max_guests: number;
  guests_included: number;
  extra_guest_minor: number;
  extra_guest_per: string;
  cleaning_fee_minor: number;
};

type Listing = {
  id: string;
  kind: "room" | "property" | "space";
  slug: string;
  name: string;
  parent_id: string | null;
  listed: boolean;
  sleeps: number;
  blurb: string;
  photos: string[];
  price_minor: number;
  price_eur: number | null;
  price_unit: string;
  rules: Rules;
};

type UnitDetail = Listing & {
  description: string;
  included: string[];
  house_rules: string[];
};

type QuoteLine = {
  code: string;
  label: string;
  qty: number;
  unit_price_minor: number;
  total_minor: number;
};

type Rates = {
  nights: number;
  available: boolean;
  bookable: boolean;
  taken: string[];
  refusals: Refusal[];
  total_minor: number;
  total_eur: number | null;
  lines: QuoteLine[];
};

type Held = {
  booking_id: string;
  access_token: string;
  hold_expires_at: number | null;
  total_minor: number;
  /** Absent on the reply to a fresh hold, which is always `held`. */
  status?: string;
};

/* ------------------------------------------------------------- the house */

/**
 * WHICH HOUSE, resolved exactly the way `/api/v1` resolves it.
 *
 * On a house's own domain (`rezerva.pensiunea.md`) the hostname says it and the
 * link is clean. On the shared domain it needs `?house=<slug>`, and that query
 * has to survive every internal navigation or the second page 404s — which is
 * why every link on this page is built through `href()` below rather than
 * written by hand.
 */
function houseParam(): string | null {
  return new URL(location.href).searchParams.get("house");
}

/* ------------------------------------------------------------------- bits */

/**
 * The price, in the box their own listings put it in: top-left, over the photo.
 *
 * MDL IS THE PRICE AND EURO IS THE HINT (§12). The house is paid in lei — one
 * currency in the ledger, one in the reports, no question ever put to maib — so
 * the euro figure sits underneath, smaller, marked „aprox.", and is never the
 * number in the box.
 */
function PriceTag({ unit, t }: { unit: Listing; t: RentCopy }) {
  if (unit.price_minor <= 0) return null;
  const per = unit.price_unit === "day" ? t.perDay : t.perNight;
  return (
    <div className="absolute left-0 top-4 bg-paper px-4 py-2.5 shadow-soft">
      <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)", lineHeight: 1.15 }}>
        {lei(unit.price_minor)} / {per}
      </p>
      {unit.price_eur !== null && (
        <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)", lineHeight: 1.3 }}>
          {t.approx} {unit.price_eur} €
        </p>
      )}
    </div>
  );
}

/**
 * The gallery, as a scroll-snap strip.
 *
 * NO CAROUSEL LIBRARY, and not out of thrift: a native overflow strip with
 * `scroll-snap-type` is what a thumb already knows how to use, it works before
 * any JavaScript has hydrated, and it costs nothing on a connection whose whole
 * budget is being spent on the photographs themselves. The arrows are for the
 * desktop pointer, which has no swipe.
 *
 * `loading="lazy"` on everything but the first: six photographs of a house at
 * 1024px are the heaviest thing on this page by an order of magnitude, and the
 * guest sees one of them.
 */
function Gallery({ photos, alt, t }: { photos: string[]; alt: string; t: RentCopy }) {
  const strip = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);

  const go = (delta: number) => {
    const el = strip.current;
    if (!el) return;
    const next = Math.min(photos.length - 1, Math.max(0, at + delta));
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    setAt(next);
  };

  if (photos.length === 0) {
    return <div className="aspect-[3/2] w-full bg-dove-100" aria-hidden="true" />;
  }

  return (
    <div className="relative">
      <div
        ref={strip}
        onScroll={(e) => {
          const el = e.currentTarget;
          setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
        }}
        className="flex w-full snap-x snap-mandatory overflow-x-auto"
        style={{ scrollbarWidth: "none" }}
      >
        {photos.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${alt} — ${t.photo} ${i + 1} ${t.of} ${photos.length}`}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            className="aspect-[3/2] w-full shrink-0 snap-center object-cover"
          />
        ))}
      </div>

      {photos.length > 1 && (
        <>
          {/* Hidden from a touch screen, which swipes. */}
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-between px-3 sm:flex">
            {([-1, 1] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => go(d)}
                aria-label={d < 0 ? "‹" : "›"}
                disabled={d < 0 ? at === 0 : at === photos.length - 1}
                className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-paper/90 text-ink shadow-soft disabled:opacity-0"
              >
                {d < 0 ? "‹" : "›"}
              </button>
            ))}
          </div>
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
            {photos.map((src, i) => (
              <span
                key={src}
                className={`h-1.5 w-1.5 rounded-full ${i === at ? "bg-paper" : "bg-paper/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Check In, Check Out, Persoane — the form their page opens with.
 *
 * NATIVE `<input type="date">`, and that is a decision rather than a shortcut. A
 * hand-built calendar is a month of work to make as good as the one already in
 * the phone: it knows the guest's locale, it opens as a wheel on iOS, it is
 * reachable with a thumb, and it is what every other booking they have ever made
 * used. `min` is TODAY IN CHISINAU, not on the phone — see `todayHere`.
 */
function DateFields({
  from, to, guests, showGuests, t, onFrom, onTo, onGuests,
}: {
  from: string; to: string; guests: number; showGuests: boolean; t: RentCopy;
  onFrom: (v: string) => void; onTo: (v: string) => void; onGuests: (n: number) => void;
}) {
  const today = todayHere();
  const field = "flex w-full items-center justify-between border border-dove-200 bg-paper px-4 tap";

  return (
    <div className="flex flex-col gap-3">
      <label className={field}>
        <span className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>{t.checkIn}</span>
        <input
          type="date" value={from} min={today}
          onChange={(e) => onFrom(e.target.value)}
          className="bg-transparent text-right text-ink outline-none"
          style={{ fontSize: "var(--text-base)" }}
        />
      </label>

      <label className={field}>
        <span className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>{t.checkOut}</span>
        <input
          type="date" value={to}
          /* A departure can never precede an arrival, and the picker should say
             so before the server has to. */
          min={from ? addDays(from, 1) : addDays(today, 1)}
          onChange={(e) => onTo(e.target.value)}
          className="bg-transparent text-right text-ink outline-none"
          style={{ fontSize: "var(--text-base)" }}
        />
      </label>

      {showGuests && (
        <label className={field}>
          <span className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>{t.guests}</span>
          <input
            type="number" inputMode="numeric" min={1} max={300} value={guests || ""}
            onChange={(e) => onGuests(Number(e.target.value))}
            className="w-20 bg-transparent text-right text-ink outline-none"
            style={{ fontSize: "var(--text-base)" }}
          />
        </label>
      )}
    </div>
  );
}

/** The house's refusals, listed plainly. Never red — the brand has no alarm colours. */
function Refusals({ list, lang }: { list: Refusal[]; lang: string }) {
  if (list.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {list.map((r) => (
        <li key={r.code} className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
          {refusalText(lang, r)}
        </li>
      ))}
    </ul>
  );
}

/** „22 aug. → 23 aug.", the stay said back to the guest in their own language. */
function humanRange(from: string, to: string, lang: string): string {
  if (!from || !to) return "";
  return `${shortDay(lang, from)} → ${shortDay(lang, to)}`;
}

/* ------------------------------------------------------------------ list */

function UnitCard({
  unit, t, lang, taken, href,
}: {
  unit: Listing; t: RentCopy; lang: string; taken: boolean | null; href: (u: Listing) => string;
}) {
  const capacity = unit.rules.max_guests || unit.sleeps;
  return (
    <article className="flex flex-col gap-5 sm:flex-row sm:gap-8">
      <div className="relative sm:w-[46%] sm:shrink-0">
        <a href={href(unit)} aria-label={unit.name}>
          <Gallery photos={unit.photos} alt={unit.name} t={t} />
        </a>
        <PriceTag unit={unit} t={t} />
      </div>

      <div className="flex flex-col gap-3 sm:pt-2">
        <h2 className="uppercase" style={{ fontSize: "var(--text-xl)", letterSpacing: "0.02em" }}>
          <a href={href(unit)}>{unit.name}</a>
        </h2>

        {unit.blurb && <p className="text-ink-muted">{unit.blurb}</p>}

        {capacity > 0 && (
          <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
            {t.upTo} {capacity} {t.people}
          </p>
        )}

        {/* Availability appears ONLY once the guest has asked. A card that says
            „Liber" before any dates exist is a claim about nothing. */}
        {taken !== null && (
          <p
            className={taken ? "text-ink-faint" : "text-olive-600"}
            style={{ fontSize: "var(--text-base)" }}
          >
            {taken ? t.taken : t.free}
          </p>
        )}

        <a
          href={href(unit)}
          className="tap mt-1 inline-flex w-fit items-center gap-2 border-b border-clay-500 pb-1 text-ink"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t.more} <span aria-hidden="true">›</span>
        </a>
      </div>
    </article>
  );
}

/* ---------------------------------------------------------------- the app */

export function Rent({
  slug, lang, setLang,
}: {
  slug: string | null; lang: string; setLang: (l: string) => void;
}) {
  const t = copy(lang);
  const house = useMemo(houseParam, []);
  const q = house ? `house=${encodeURIComponent(house)}&` : "";
  const href = useCallback(
    (u: { slug: string }) => `/r/${encodeURIComponent(u.slug)}${house ? `?house=${encodeURIComponent(house)}` : ""}`,
    [house]
  );

  const [units, setUnits] = useState<Listing[] | null>(null);
  const [detail, setDetail] = useState<UnitDetail | null | "missing">(null);
  const [failed, setFailed] = useState(false);

  /* The dates survive the walk from the list into a unit's own page: a guest who
     typed them once should not retype them because they tapped „Vezi mai
     multe". sessionStorage, not local — these are this visit's dates. */
  const [from, setFrom] = useState(() => sessionStorage.getItem("prispa.rent.from") ?? "");
  const [to, setTo] = useState(() => sessionStorage.getItem("prispa.rent.to") ?? "");
  const [guests, setGuests] = useState(() => Number(sessionStorage.getItem("prispa.rent.guests")) || 0);

  useEffect(() => {
    sessionStorage.setItem("prispa.rent.from", from);
    sessionStorage.setItem("prispa.rent.to", to);
    sessionStorage.setItem("prispa.rent.guests", String(guests || ""));
  }, [from, to, guests]);

  /*
   * A NEW QUESTION VOIDS THE OLD ANSWER. Changing the dates left every card
   * still labelled „Liber" or „Ocupat" for the window the guest had just moved
   * off — the most confident possible way to be wrong. Null is „nothing has been
   * asked", which is what the cards draw before the first check.
   */
  useEffect(() => { setTakenIds(null); }, [from, to]);

  /** Which units are gone in the chosen window — null until anybody asks. */
  const [takenIds, setTakenIds] = useState<Set<string> | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let alive = true;
    const path = slug
      ? `/api/v1/units/${encodeURIComponent(slug)}?${q}lang=${lang}`
      : `/api/v1/units?${q}lang=${lang}`;

    fetch(path)
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 404) {
          if (slug) setDetail("missing");
          else setFailed(true);
          return;
        }
        if (!res.ok) return setFailed(true);
        const body = await res.json();
        if (!alive) return;
        if (slug) setDetail(body.unit as UnitDetail);
        else setUnits((body.units as Listing[]).filter((u) => u.listed));
      })
      .catch(() => alive && setFailed(true));

    return () => { alive = false; };
  }, [slug, lang, q]);

  /**
   * „Verifică disponibilitatea" on the LIST: one request for every card.
   *
   * `/availability` rather than one `/rates` per unit — a house with six units
   * would otherwise cost six round trips to answer one question, on the
   * connection this whole page is drawn for.
   */
  const checkList = async () => {
    if (!from || !to || nightCount(from, to) < 1) return;
    setChecking(true);
    try {
      const res = await fetch(`/api/v1/availability?${q}from=${from}&to=${to}`);
      /*
       * `res.ok` IS CHECKED, because the failure mode is the expensive
       * direction. A 400 or a 500 parsed straight into `Object.keys(undefined
       * ?? {})` gave an EMPTY set, which this screen draws as „Liber" on every
       * card — an availability claim made from an error. Null means „no claim",
       * which is what the cards then show.
       */
      const body = res.ok ? ((await res.json()) as { taken?: Record<string, string[]> }) : null;
      setTakenIds(body?.taken ? new Set(Object.keys(body.taken)) : null);
    } catch {
      setTakenIds(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-dvh bg-linen">
      <header className="border-b border-dove-200 bg-paper">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-4">
          <a href={`/r${house ? `?house=${encodeURIComponent(house)}` : ""}`}
             style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
            {t.title}
          </a>
          <nav className="flex shrink-0 border border-dove-400" aria-label="Limbă">
            {LANGS.map(([code, label]) => (
              <button
                key={code} onClick={() => setLang(code)} aria-pressed={lang === code}
                className={`px-2.5 ${lang === code ? "bg-clay-500 text-paper" : "bg-paper text-ink-muted"}`}
                style={{ borderRadius: 0, fontSize: "var(--text-kicker)", minHeight: 44, minWidth: 40 }}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        {failed && <p className="text-ink-muted">{t.notFound}</p>}

        {/* ------------------------------------------------------- one unit */}
        {slug && detail === "missing" && <p className="text-ink-muted">{t.notFound}</p>}
        {slug && detail && detail !== "missing" && (
          <UnitPage
            unit={detail} t={t} lang={lang} q={q}
            from={from} to={to} guests={guests}
            onFrom={setFrom} onTo={setTo} onGuests={setGuests}
            backHref={`/r${house ? `?house=${encodeURIComponent(house)}` : ""}`}
          />
        )}

        {/* ---------------------------------------------------------- list */}
        {!slug && (
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
            <aside className="flex flex-col gap-4 lg:sticky lg:top-8 lg:w-[300px] lg:shrink-0">
              <DateFields
                from={from} to={to} guests={guests} showGuests={false} t={t}
                onFrom={setFrom} onTo={setTo} onGuests={setGuests}
              />
              <button
                type="button"
                onClick={() => void checkList()}
                disabled={checking || nightCount(from, to) < 1}
                className="tap w-full bg-clay-500 px-6 text-paper disabled:opacity-50"
                style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)" }}
              >
                {checking ? t.checking : t.check}
              </button>
            </aside>

            <div className="flex flex-1 flex-col gap-14">
              {units?.length === 0 && <p className="text-ink-muted">{t.noUnits}</p>}
              {units?.map((u) => (
                <UnitCard
                  key={u.id} unit={u} t={t} lang={lang} href={href}
                  taken={takenIds === null ? null : takenIds.has(u.id)}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------- one unit */

function UnitPage({
  unit, t, lang, q, from, to, guests, onFrom, onTo, onGuests, backHref,
}: {
  unit: UnitDetail; t: RentCopy; lang: string; q: string;
  from: string; to: string; guests: number;
  onFrom: (v: string) => void; onTo: (v: string) => void; onGuests: (n: number) => void;
  backHref: string;
}) {
  const [rates, setRates] = useState<Rates | null>(null);
  const [held, setHeld] = useState<Held | null>(null);

  /**
   * EVERY NIGHT THIS UNIT HAS LOST, FOR A YEAR, IN ONE REQUEST.
   *
   * The calendar cannot grey out an occupied night it has not been told about,
   * and asking per visible month would put a round trip behind the „next month"
   * arrow — on the connection this page is drawn for. A year of a guesthouse's
   * taken nights is a few hundred short strings; `/availability` caps the window
   * at 366 nights, which is exactly the year this asks for.
   *
   * IT ASKS ABOUT THE UNIT, AND GETS THE HIERARCHY FOR FREE: taking a room
   * writes a row for the house above it too, so a house whose Camera 1 is sold
   * reports its own nights gone without this page knowing hierarchies exist.
   */
  /*
   * A RESERVATION FOUND AGAIN, from the link in the address bar.
   *
   * The booking form writes `?b=<id>&t=<token>` on success, and the token is the
   * ONLY credential this reservation has (§6) - there is no account to log into
   * and no cookie to carry it. So the link has to be the way back, or a reload,
   * a share to a partner, or a return from the mail app lands the guest on the
   * empty form of a stay they have already booked.
   *
   * `/api/v1/bookings/:id?token=` answers 404 to a wrong or missing token, which
   * is also what a stale link out of somebody's history gets: the form, which is
   * the right thing to show them.
   *
   * THIS WAS DELETED BY THE SAME BULK EDIT that took the quote effect, and was
   * found by the contract test written after the first one - which is the whole
   * argument for that test existing.
   */
  useEffect(() => {
    const url = new URL(location.href);
    const id = url.searchParams.get("b");
    const token = url.searchParams.get("t");
    if (!id || !token) return;

    let alive = true;
    fetch(`/api/v1/bookings/${encodeURIComponent(id)}?${q}token=${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!alive || !body) return;
        setHeld({
          booking_id: body.booking_id,
          access_token: token,
          hold_expires_at: body.hold_expires_at,
          total_minor: body.total_minor,
          status: body.status,
        });
      })
      .catch(() => { /* offline — the form is still there, and the link works later */ });
    return () => { alive = false; };
  }, [q]);

  const [taken, setTaken] = useState<Set<string>>(() => new Set());

  /*
   * THE PARTY STARTS AT THE SMALLEST ONE THIS UNIT WILL ACCEPT.
   *
   * The box was empty, and empty is not neutral: the page quoted for `guests ||
   * 1`, so a guest reading „65.975,00 MDL" was reading the price for ONE person
   * on a house that will not take fewer than eight — and the refusal „pentru
   * grupuri de la 8 persoane" appeared under a total computed for a party they
   * had never named. On their own flagship unit the first honest number is 8,
   * and on the garden it is 1.
   *
   * Only when nothing has been chosen yet: a guest who typed 12 on the list page
   * and walked into a unit keeps their 12.
   */
  useEffect(() => {
    if (guests > 0) return;
    onGuests(Math.max(1, unit.rules.min_guests));
    // Once per unit, on arrival. `guests` is deliberately not a dependency —
    // this seeds a default, it does not police the field afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id]);

  useEffect(() => {
    let alive = true;
    const start = todayHere();
    fetch(`/api/v1/availability?${q}unit=${encodeURIComponent(unit.slug)}&from=${start}&to=${addDays(start, 364)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { taken?: Record<string, string[]> } | null) => {
        if (!alive || !body?.taken) return;
        setTaken(new Set(Object.values(body.taken).flat()));
      })
      .catch(() => { /* offline — the server refuses the clash on submit anyway */ });
    return () => { alive = false; };
  }, [unit.slug, q, held]);

  /*
   * THE QUOTE FOLLOWS THE FIELDS, with a short pause.
   *
   * Typing a date or a party size fires `change` on every keystroke, and each
   * one would be a request from a phone on 3G. 400ms is long enough that a
   * finished number is one request and short enough that the total appears to
   * follow the tap.
   *
   * DELETED ONCE, BY A CARELESS BULK EDIT, and the failure is worth recording:
   * `rates` stayed declared and simply stopped being SET, so the file still
   * compiled, the tests still passed, and the page silently lost its price -
   * dates chosen, everything drawn, no total anywhere. Nothing typed-checked it
   * because "never assigned" is a valid program.
   */
  useEffect(() => {
    if (nightCount(from, to) < 1) { setRates(null); return; }
    let alive = true;
    const timer = setTimeout(() => {
      /*
       * QUOTE THE PARTY THE HOLD WILL POST, which is `guests || 1`.
       *
       * Asking without one made `/rates` answer `bookable: true` - a missing
       * party is „not told yet" there - while the hold then posted `adults: 1`
       * and was refused. Same number to both routes, so the refusal appears
       * before the tap rather than after it.
       */
      const g = `&guests=${guests || 1}`;
      fetch(`/api/v1/rates?${q}unit=${encodeURIComponent(unit.slug)}&from=${from}&to=${to}${g}&lang=${lang}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((body: Rates | null) => alive && setRates(body))
        .catch(() => alive && setRates(null));
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [unit.slug, from, to, guests, lang, q]);

  if (held) return <HeldPanel held={held} unit={unit} t={t} backHref={backHref} />;

  const nights = nightCount(from, to);

  return (
    <article className="flex flex-col gap-8">
      <a href={backHref} className="tap inline-flex w-fit items-center gap-2 text-ink-muted"
         style={{ fontSize: "var(--text-kicker)" }}>
        <span aria-hidden="true">‹</span> {t.back}
      </a>

      <div className="relative">
        <Gallery photos={unit.photos} alt={unit.name} t={t} />
        <PriceTag unit={unit} t={t} />
      </div>

      <header className="flex flex-col gap-3">
        <h1 className="uppercase" style={{ fontSize: "var(--text-2xl)", letterSpacing: "0.02em" }}>
          {unit.name}
        </h1>
        {unit.blurb && <p className="text-ink-muted" style={{ fontSize: "var(--text-lg)" }}>{unit.blurb}</p>}
      </header>

      <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
        <div className="flex flex-1 flex-col gap-8">
          {unit.description && (
            <div className="flex flex-col gap-4">
              {unit.description.split(/\n\s*\n/).map((para) => (
                <p key={para.slice(0, 40)} className="text-ink-muted">{para}</p>
              ))}
            </div>
          )}

          {unit.included.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 style={{ fontSize: "var(--text-lg)" }}>{t.included}</h2>
              <ul className="flex flex-wrap gap-2">
                {unit.included.map((x) => (
                  <li key={x} className="border border-dove-200 bg-paper px-3 py-1.5 text-ink-muted"
                      style={{ fontSize: "var(--text-kicker)" }}>
                    {x}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {unit.house_rules.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 style={{ fontSize: "var(--text-lg)" }}>{t.rules}</h2>
              <ul className="flex flex-col gap-1.5">
                {unit.house_rules.map((x) => (
                  <li key={x} className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>
                    · {x}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* THE BOOKING COLUMN. On a phone it is simply the next thing down the
            page; on a desktop it stays beside the text, which is where their
            own listings put it. */}
        {/* 400px, not 380: seven day cells plus the box's own padding, so the
            calendar is sized by what it has to hold rather than by a round
            number that happened to be typed first. */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-8 lg:w-[400px] lg:shrink-0">
          {/*
            * THE CALENDAR KNOWS WHAT IT CANNOT SELL, so a refusal arrives before
            * the tap rather than after a round trip. See src/guest/RangeCalendar.tsx
            * — the occupied nights come from /api/v1/availability, the weekday and
            * notice rules travel with the unit, and choosing an arrival closes
            * every earlier day for the departure.
            */}
          <RangeCalendar
            from={from} to={to} taken={taken} rules={unit.rules} t={t} lang={lang}
            months={1}
            onPick={(a, b) => { onFrom(a); onTo(b); }}
          />

          <div className="flex flex-col gap-4 border border-dove-200 bg-paper p-5">
            <div className="flex items-center justify-between gap-3">
              <p style={{ fontFamily: "var(--font-display)" }}>
                {nights > 0
                  ? `${humanRange(from, to, lang)} · ${nightsPhrase(lang, nights)}`
                  : t.pickDates}
              </p>
              {nights > 0 && (
                <button
                  type="button"
                  onClick={() => { onFrom(""); onTo(""); }}
                  className="text-ink-faint underline"
                  style={{ fontSize: "var(--text-kicker)" }}
                >
                  {t.changeDates}
                </button>
              )}
            </div>

            <label className="flex w-full items-center justify-between border border-dove-200 bg-paper px-4 tap">
              <span className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>{t.guests}</span>
              {/* The bounds are the HOUSE'S, so the stepper cannot walk a guest
                  into a number the server will refuse: 8 to 14 on their
                  pensiune, 1 to 300 in the garden. */}
              <input
                type="number" inputMode="numeric"
                min={Math.max(1, unit.rules.min_guests)}
                max={unit.rules.max_guests || 300}
                value={guests || ""}
                onChange={(e) => onGuests(Number(e.target.value))}
                className="w-20 bg-transparent text-right text-ink outline-none"
                style={{ fontSize: "var(--text-base)" }}
              />
            </label>

          {rates && (
            <>
              <Refusals list={rates.refusals} lang={lang} />

              {!rates.available && rates.nights > 0 && (
                <p className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>{t.taken}</p>
              )}

              {rates.lines.length > 0 && (
                <dl className="flex flex-col gap-2 border-t border-dove-200 pt-4">
                  {rates.lines.map((l) => (
                    <div key={l.code} className="flex items-baseline justify-between gap-4">
                      <dt className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                        {l.label}{l.qty > 1 ? ` × ${l.qty}` : ""}
                      </dt>
                      <dd className="tabular text-ink">{lei(l.total_minor)}</dd>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between gap-4 border-t border-dove-200 pt-2">
                    <dt style={{ fontFamily: "var(--font-display)" }}>{t.total}</dt>
                    <dd className="tabular" style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
                      {lei(rates.total_minor)}
                    </dd>
                  </div>
                  {rates.total_eur !== null && (
                    <p className="text-right text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                      {t.approx} {rates.total_eur} €
                    </p>
                  )}
                </dl>
              )}
            </>
          )}

            <BookingForm
              unit={unit} t={t} q={q} lang={lang}
              from={from} to={to} guests={guests}
              enabled={!!rates?.bookable}
              onHeld={setHeld}
            />
          </div>
        </aside>
      </div>
    </article>
  );
}

/* --------------------------------------------------------------- the form */

function BookingForm({
  unit, t, q, lang, from, to, guests, enabled, onHeld,
}: {
  unit: UnitDetail; t: RentCopy; q: string; lang: string;
  from: string; to: string; guests: number; enabled: boolean;
  onHeld: (h: Held) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /*
   * ONE KEY PER FORM, minted once.
   *
   * A phone on 3G taps „Rezervă" twice and a browser retries a POST it never saw
   * an answer to; without this, that is two reservations for one guest and a
   * room nobody is coming to sleep in. Minted at mount rather than per attempt
   * because a REFUSED attempt writes nothing — there is no booking for a replay
   * to collide with — while a SUCCEEDED one leaves this screen for good.
   */
  const idempotencyKey = useMemo(() => `rent-${crypto.randomUUID()}`, []);

  const field = "w-full border border-dove-200 bg-paper px-4 py-3 text-ink outline-none focus:border-clay-500";

  async function submit() {
    setProblem(null);
    if (!name.trim()) return setProblem(t.nameNeeded);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setProblem(t.emailNeeded);

    setSending(true);
    try {
      const res = await fetch(`/api/v1/holds${q ? `?${q.slice(0, -1)}` : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(q ? { house: new URLSearchParams(q).get("house") } : {}),
          idempotency_key: idempotencyKey,
          lang,
          nights: [{ unit: unit.slug, from, to }],
          guest: {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            // The rules are checked against the WHOLE party, and this page asks
            // for one number rather than an adults/children split: age bands are
            // a pricing feature the house has no screen for yet (B-7), and a
            // „Copii" field that changed no price would be a question asked for
            // nothing.
            adults: guests || 1,
            kids: 0,
            note: note.trim() || null,
          },
        }),
      });

      if (res.status === 409) return setProblem(t.somebodyTook);
      if (!res.ok) return setProblem(t.failed);

      const body = (await res.json()) as Held;
      /*
       * THE TOKEN GOES INTO THE URL, and it is the only copy the guest has.
       * Their reservation is not behind a login — the token IS the credential
       * (§6) — so a reload, a share to a partner, or coming back from a mail app
       * has to find it. Replacing the entry rather than pushing keeps Back
       * pointing where it did.
       */
      if (body.access_token) {
        const url = new URL(location.href);
        url.searchParams.set("b", body.booking_id);
        url.searchParams.set("t", body.access_token);
        history.replaceState(null, "", url.toString());
      }
      onHeld(body);
    } catch {
      setProblem(t.failed);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 border-t border-dove-200 pt-4">
      <h2 style={{ fontSize: "var(--text-lg)" }}>{t.yourDetails}</h2>

      <input className={field} value={name} onChange={(e) => setName(e.target.value)}
             placeholder={t.name} autoComplete="name" />
      <input className={field} value={email} onChange={(e) => setEmail(e.target.value)}
             placeholder={t.email} type="email" inputMode="email" autoComplete="email" />
      <input className={field} value={phone} onChange={(e) => setPhone(e.target.value)}
             placeholder={`${t.phone} (${t.optional})`} type="tel" autoComplete="tel" />
      <textarea className={field} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={`${t.note} (${t.optional})`} rows={3} />

      {problem && (
        <p className="text-ink" style={{ fontSize: "var(--text-kicker)" }}>{problem}</p>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={!enabled || sending}
        className="tap w-full bg-clay-500 px-6 text-paper disabled:opacity-50"
        style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)" }}
      >
        {sending ? t.booking : t.book}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------- after the hold */

/**
 * WHAT THE GUEST SEES WHEN IT WORKED — and what it does NOT claim.
 *
 * There is no „Plătește acum" here, because there is no payment route on
 * `/api/v1` yet: the money path is frozen and gets its own commit. The honest
 * version of that is to say so — the nights ARE held, nothing has been charged,
 * and the house makes contact — rather than to show a button that leads nowhere
 * or, worse, to imply the stay is paid for.
 */
function HeldPanel({
  held, unit, t, backHref,
}: {
  held: Held; unit: UnitDetail; t: RentCopy; backHref: string;
}) {
  const expires = held.hold_expires_at;
  const [left, setLeft] = useState(() =>
    expires ? expires - Math.floor(Date.now() / 1000) : 0
  );

  useEffect(() => {
    if (!expires) return;
    const tick = () => {
      const next = expires - Math.floor(Date.now() / 1000);
      setLeft(next);
      // Stop at zero rather than counting into negative minutes for ever.
      if (next <= 0) clearInterval(timer);
    };
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expires]);

  const minutes = Math.max(0, Math.ceil(left / 60));
  /*
   * THE STATE IS READ, NOT ASSUMED. A fresh hold has no `status` field and is
   * always held; a reservation reopened from its link an hour later may have
   * been confirmed by the house, or may have lapsed — and „Rezervare
   * înregistrată" over a stay whose nights went back to the calendar is the one
   * thing this screen must never say.
   */
  const status = held.status ?? "held";
  /*
   * AN ELAPSED HOLD IS DEAD, whatever the row said when this screen loaded.
   *
   * The status is read once, at mount, and a hold that runs out while the guest
   * is looking at it kept saying „Rezervare înregistrată" over nights that had
   * gone back to the calendar — the exact thing this panel's own note says it
   * must never do. `expires` is null on a CONFIRMED booking, so the elapsed test
   * is guarded on the hold still being one.
   */
  const lapsed = status === "held" && !!expires && left <= 0;
  const dead = status === "cancelled" || status === "no_show" || lapsed;

  return (
    <section className="mx-auto flex max-w-xl flex-col gap-6 border border-dove-200 bg-paper p-6">
      <h1 style={{ fontSize: "var(--text-2xl)" }}>
        {dead ? t.lapsed : status === "confirmed" ? t.confirmed : t.held}
      </h1>

      <div className="flex flex-col gap-1">
        <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>{unit.name}</p>
        <p className="tabular text-ink-muted">{lei(held.total_minor)}</p>
      </div>

      {!dead && left > 0 && (
        <p className="text-ink-muted" style={{ fontSize: "var(--text-base)" }}>
          {t.heldFor}: {minutes} {t.minutes}
        </p>
      )}

      <div className="flex flex-col gap-2 border-t border-dove-200 pt-4">
        <h2 style={{ fontSize: "var(--text-lg)" }}>{dead ? t.lapsed : t.whatNext}</h2>
        <p className="text-ink-muted">{dead ? t.lapsedBody : t.whatNextBody}</p>
      </div>

      <div className="flex flex-col gap-1 border-t border-dove-200 pt-4">
        <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{t.yourCode}</p>
        <p className="tabular break-all text-ink">{held.booking_id}</p>
        {/* Only when the link actually carries the token. On an idempotent
            replay the server does not re-issue it (it exists in one response,
            once), so there is nothing in the address bar to keep. */}
        {held.access_token && (
          <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{t.keepCode}</p>
        )}
      </div>

      <a href={backHref} className="tap inline-flex items-center gap-2 text-ink-muted"
         style={{ fontSize: "var(--text-kicker)" }}>
        <span aria-hidden="true">‹</span> {t.back}
      </a>
    </section>
  );
}
